import { useState, useMemo } from 'react'
import { 
  TrendingUp, 
  Eye, 
  Download, 
  Share, 
  Shield, 
  Globe, 
  Users, 
  Activity,
  BarChart3,
  FileText,
  Image,
  Video,
  Music,
  Upload
} from 'lucide-react'
import { useContent } from '@/hooks/useContent'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useTenant } from '@/contexts/TenantContext'
import ContentGrid from '@/components/content/ContentGrid'
import AnalyticsDashboard from '@/components/analytics/AnalyticsDashboard'
import { formatFileSize } from '@/lib/utils'

const ClientDashboard: React.FC = () => {
  const { currentTenant } = useTenant()
  const { useUserContent } = useContent()
  const { useTenantAnalytics } = useAnalytics()
  const [activeTab, setActiveTab] = useState<'overview' | 'content' | 'analytics'>('overview')

  // Fetch data with React Query
  const { data: contentData, isLoading: contentLoading } = useUserContent()
  const { data: analyticsData, isLoading: analyticsLoading } = useTenantAnalytics(
    currentTenant?.id || '',
    '7d'
  )

  // Memoized computed data
  const dashboardStats = useMemo(() => {
    if (!contentData?.data?.data) return null

    const content = contentData.data.data
    const totalSize = content.reduce((sum, item) => sum + (item.metadata?.size || 0), 0)
    const fileTypes = content.reduce((acc, item) => {
      const type = item.metadata?.mimeType || ''
      if (type.startsWith('image/')) acc.images++
      else if (type.startsWith('video/')) acc.videos++
      else if (type.startsWith('audio/')) acc.audio++
      else acc.documents++
      return acc
    }, { images: 0, videos: 0, audio: 0, documents: 0 })

    return {
      totalContent: content.length,
      totalSize,
      fileTypes,
      protectedContent: content.filter(item => item.metadata?.fingerprint).length,
      watermarkedContent: content.filter(item => item.metadata?.watermarkData).length
    }
  }, [contentData])

  const analyticsStats = useMemo(() => {
    if (!analyticsData) return null

    return {
      views: analyticsData.views || 0,
      downloads: analyticsData.downloads || 0,
      shares: analyticsData.shares || 0,
      revenue: analyticsData.revenue || 0
    }
  }, [analyticsData])

  if (contentLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Client Dashboard</h1>
        <p className="text-gray-600 mt-1">Enterprise content management and analytics</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'content', label: 'Content', icon: FileText },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp }
          ].map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Content</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {dashboardStats?.totalContent || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Views</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analyticsStats?.views.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Shield className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Protected</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {dashboardStats?.protectedContent || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Globe className="h-6 w-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Watermarked</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {dashboardStats?.watermarkedContent || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Content Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Content Distribution</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Image className="h-5 w-5 text-blue-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Images</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {dashboardStats?.fileTypes.images || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Video className="h-5 w-5 text-red-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Videos</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {dashboardStats?.fileTypes.videos || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Music className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Audio</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {dashboardStats?.fileTypes.audio || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-gray-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Documents</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {dashboardStats?.fileTypes.documents || 0}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Storage Usage</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Used Storage</span>
                    <span className="text-gray-900">
                      {formatFileSize(dashboardStats?.totalSize || 0)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${Math.min((dashboardStats?.totalSize || 0) / (100 * 1024 * 1024 * 1024) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  {formatFileSize(dashboardStats?.totalSize || 0)} of 100 GB used
                </p>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {contentData?.data?.data?.slice(0, 5).map((item, index) => (
                <div key={item.id} className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <Activity className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.metadata?.title || item.originalFilename}
                    </p>
                    <p className="text-xs text-gray-500">
                      Uploaded {new Date(item.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <span>{formatFileSize(item.metadata?.size || 0)}</span>
                    {item.metadata?.fingerprint && (
                      <Shield className="h-3 w-3 text-blue-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content Tab */}
      {activeTab === 'content' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Your Content</h2>
            <div className="flex space-x-2">
              <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                Filter
              </button>
              <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
                Upload New
              </button>
            </div>
          </div>

          {contentData?.data?.data ? (
            <ContentGrid 
              content={contentData.data.data}
              variant="grid"
              showActions={true}
            />
          ) : (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500">No content uploaded yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <AnalyticsDashboard tenantId={currentTenant?.id} />
      )}
    </div>
  )
}

export default ClientDashboard 